import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useFloating } from '@/shared/hooks/useFloating';

const fadeIn = keyframes`from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}`;

const Wrap = styled.div`display:inline-flex;flex-direction:column;width:100%;min-width:0;`;

const Trigger = styled.button<{ $hasValue: boolean }>`
  display:flex;align-items:center;gap:6px;width:100%;min-width:0;min-height:34px;
  padding:4px 10px;border-radius:7px;border:1px solid var(--border-subtle);
  background:var(--bg-primary);color:${({ $hasValue }) => ($hasValue ? 'var(--text-primary)' : 'var(--text-tertiary)')};
  font-size:13px;font-family:inherit;cursor:pointer;text-align:left;
  transition:border-color 0.12s ease;flex-wrap:wrap;overflow:hidden;
  &:focus{outline:none;border-color:var(--accent);}
  &:hover{border-color:var(--accent);}
`;

const SelectedChip = styled.span`
  display:inline-flex;align-items:center;gap:3px;padding:2px 6px;
  border-radius:4px;background:var(--accent-muted);color:var(--accent);
  font-size:11px;font-weight:500;white-space:nowrap;
  max-width:100%;min-width:0;
`;

const SelectedChipLabel = styled.span`
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0;flex:1;
`;

const RemoveBtn = styled.span`
  cursor:pointer;font-size:10px;line-height:1;opacity:0.7;
  &:hover{opacity:1;}
`;

const Chevron = styled.span<{ $open: boolean }>`
  margin-left:auto;display:flex;align-items:center;flex-shrink:0;
  transition:transform 0.12s ease;
  transform:${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
`;

const Panel = styled.div<{ $x: number; $y: number; $w: number; $dir: 'up' | 'down' }>`
  position:absolute;z-index:9999;
  left:${({ $x }) => $x}px;
  top:${({ $dir, $y }) => $dir === 'down' ? `${$y + 4}px` : 'auto'};
  bottom:${({ $dir, $y }) => $dir === 'up' ? `${window.innerHeight - $y + 4}px` : 'auto'};
  width:${({ $w }) => Math.max($w, 200)}px;
  background:var(--bg-surface);border:1px solid var(--border-subtle);border-radius:10px;
  box-shadow:0 12px 40px rgba(0,0,0,0.22);padding:4px;
  animation:${fadeIn} 0.12s ease;max-height:280px;overflow:hidden;
  display:flex;flex-direction:column;
`;

const SearchInput = styled.input`
  height:32px;border-radius:6px;border:1px solid var(--border-subtle);
  background:var(--bg-primary);color:var(--text-primary);padding:0 10px;
  font-size:12px;font-family:inherit;margin:4px 4px 6px;flex-shrink:0;
  &:focus{outline:none;border-color:var(--accent);}
  &::placeholder{color:var(--text-tertiary);}
`;

const OptionsList = styled.div`overflow-y:auto;flex:1;`;

const Option = styled.button<{ $checked?: boolean }>`
  display:flex;align-items:center;gap:8px;width:100%;
  padding:7px 10px;border-radius:6px;border:none;text-align:left;
  font-size:13px;font-family:inherit;cursor:pointer;
  transition:all 0.06s ease;
  background:${({ $checked }) => ($checked ? 'var(--accent-muted)' : 'transparent')};
  color:var(--text-primary);
  &:hover{background:var(--bg-hover);}
`;

const Checkbox = styled.span<{ $on: boolean }>`
  width:16px;height:16px;border-radius:4px;flex-shrink:0;
  border:1.5px solid ${({ $on }) => ($on ? 'var(--accent)' : 'var(--border-subtle)')};
  background:${({ $on }) => ($on ? 'var(--accent)' : 'transparent')};
  display:flex;align-items:center;justify-content:center;
  transition:all 0.08s ease;
  svg{opacity:${({ $on }) => ($on ? 1 : 0)};transition:opacity 0.08s ease;}
`;

const OptionDot = styled.span<{ $c: string }>`width:8px;height:8px;border-radius:50%;background:${({ $c }) => $c};flex-shrink:0;`;

const EmptyMsg = styled.div`padding:16px;text-align:center;font-size:12px;color:var(--text-tertiary);`;

export type MultiSelectOption = {
  value: string;
  label: string;
  color?: string;
};

type Props = {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  searchable?: boolean;
};

export const MultiSelect: React.FC<Props> = ({ values, onChange, options, placeholder = 'Выберите...', searchable = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { anchorRef, pos } = useFloating(open, 300);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(values), [values]);
  const selectedLabels = useMemo(() => values.map((v) => options.find((o) => o.value === v)).filter(Boolean) as MultiSelectOption[], [values, options]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setTimeout(() => searchRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (anchorRef.current?.contains(e.target as Node)) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open, anchorRef]);

  const toggle = useCallback((val: string) => {
    if (selectedSet.has(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  }, [values, onChange, selectedSet]);

  const removeChip = useCallback((val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(values.filter((v) => v !== val));
  }, [values, onChange]);

  const panel = open ? createPortal(
    <Panel ref={panelRef} $x={pos.left} $y={pos.top} $w={pos.width} $dir={pos.direction}>
      {searchable && (
        <SearchInput
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск..."
        />
      )}
      <OptionsList>
        {filtered.length === 0 && <EmptyMsg>Ничего не найдено</EmptyMsg>}
        {filtered.map((opt) => {
          const checked = selectedSet.has(opt.value);
          return (
            <Option key={opt.value} type="button" $checked={checked} onClick={() => toggle(opt.value)}>
              <Checkbox $on={checked}>
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M3 8l4 4 6-7" /></svg>
              </Checkbox>
              {opt.color && <OptionDot $c={opt.color} />}
              {opt.label}
            </Option>
          );
        })}
      </OptionsList>
    </Panel>,
    document.body,
  ) : null;

  return (
    <Wrap ref={anchorRef}>
      <Trigger type="button" $hasValue={values.length > 0} onClick={() => setOpen(!open)}>
        {selectedLabels.length > 0 ? (
          selectedLabels.map((s) => (
            <SelectedChip key={s.value} title={s.label}>
              {s.color && <OptionDot $c={s.color} />}
              <SelectedChipLabel>{s.label}</SelectedChipLabel>
              <RemoveBtn onClick={(e) => removeChip(s.value, e)}>&times;</RemoveBtn>
            </SelectedChip>
          ))
        ) : (
          <span style={{ flex: 1 }}>{placeholder}</span>
        )}
        <Chevron $open={open}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6l4 4 4-4" /></svg>
        </Chevron>
      </Trigger>
      {panel}
    </Wrap>
  );
};
